import cx from 'classnames'
import type React from 'react'
import { forwardRef, useEffect, useRef } from 'react'

import { Check, IconIndeterminate } from '../../icons'
import css from './Checkbox.module.scss'
import type { Classnames, InputType, Props } from './types'

interface IconProps {
  indeterminate: boolean
  size: 'large' | 'small'
}
const Icon: React.FC<IconProps> = ({ indeterminate, size }) => {
  return (
    <span className={css.checkbox}>
      {indeterminate ? (
        <IconIndeterminate />
      ) : (
        <Check size={size === 'large' ? 10 : 8} />
      )}
    </span>
  )
}

const Checkbox: React.ForwardRefExoticComponent<
  React.PropsWithoutRef<Props & InputType> & React.RefAttributes<{}>
> = forwardRef(
  (
    {
      classnames,
      disabled,
      name,
      label,
      onChange,
      size = 'large',
      initialValue = '',
      labelIsOnLeftSide,
      indeterminate,
      ...props
    },
    _ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = !!indeterminate
      }
    }, [indeterminate])

    const classes: Classnames = {
      container: cx(
        css.container,
        disabled && css.disabled,
        size && css[size],
        classnames?.container
      ),
      input: cx(css.input, classnames?.input, {
        [css.inverted]: labelIsOnLeftSide,
      }),
    }

    if (disabled) {
      return (
        <div className={classes.container}>
          <label className={classes.input}>
            <input {...props} type="checkbox" readOnly ref={inputRef} />
            <Icon size={size} indeterminate={!!indeterminate} />
            <span className={css.label}>{label}</span>
          </label>
        </div>
      )
    }

    return (
      <div className={classes.container}>
        <label className={classes.input}>
          <input
            {...props}
            name={name}
            type="checkbox"
            onChange={(e) => onChange?.(e.target.checked)}
            ref={inputRef}
          />
          <Icon size={size} indeterminate={!!indeterminate} />
          {label && <span className={css.label}>{label}</span>}
        </label>
      </div>
    )
  }
)

export default Checkbox
